import type { IndustryPage, IndustryPageContent } from "./types";

const en: IndustryPageContent = {
  eyebrow: "AI agents · Retail & F&B",
  h1: "AI agents for retail & F&B in Malaysia & Singapore",
  intro:
    "Retail and F&B is a thin-margin, high-volume business, and more and more of it now happens over WhatsApp rather than at the counter. An AI agent can capture orders and answer buyers around the clock while a human handles the exceptions — built on Claude, human-in-the-loop, so the judgement calls still sit with your team.",
  ctaLabel: "Scope an agent for your store",

  painHeading: "The economics of retail & F&B",
  painPoints: [
    {
      title: "Thin margins, high volume, low ticket",
      body: "You make your money on turnover, not on any single order. That means every minute a staff member spends re-keying a WhatsApp order into the POS, chasing a stock check, or answering the same question for the tenth time is margin quietly leaking away. At retail and F&B volumes, small per-order inefficiencies add up to real ringgit fast.",
    },
    {
      title: "WhatsApp is the storefront",
      body: "In Malaysia and Singapore a huge share of orders and enquiries arrive by chat — after hours, over the weekend, and all at once during peak. They come in Malay, English and Chinese, often mixed 'rojak' in a single message. When nobody is free to reply within minutes, buyers move on and the order is simply lost, unrecorded.",
    },
    {
      title: "Peaks and staff churn",
      body: "Festive seasons, paydays and promotions send volume through the roof, then it drops again. You cannot hire fast enough for the peak, and retail and F&B turnover is high — so you are forever re-training new staff on the menu, the prices and the way you like enquiries answered. The knowledge keeps walking out the door.",
    },
    {
      title: "Multi-outlet & multi-channel consistency",
      body: "Running several outlets or selling across WhatsApp, marketplaces and walk-in means the same promo, price and message has to hold everywhere, in every language. It rarely does — one outlet quotes the old price, another misses the promo end date. For bigger groups this bleeds into corporate communications, where a single wrong figure travels fast.",
    },
  ],

  useCasesHeading: "Where agents pay off in retail & F&B",
  useCasesIntro:
    "The clearest wins are taking orders on WhatsApp end to end, answering product and menu enquiries, and triaging support so the right message reaches a human fast. For multi-outlet groups, agents also keep corporate communications consistent across outlets and languages.",

  sections: [
    {
      heading: "Why agents fit this sector",
      body: "A retail or F&B agent is only useful if it reflects what you actually sell today and never oversteps on money. We build it to work from your live systems and to hand off the moment something looks off.",
      bullets: [
        "Works from your live menu, catalogue and stock, so it only offers what is actually in — no selling a sold-out item.",
        "Writes orders through connected tools (POS, order sheet, CRM) so nothing is fat-fingered on the way in.",
        "Never auto-charges a card — it sends a payment link and the customer decides; you stay in control of the money.",
        "Routes odd or high-value orders, refunds and complaints to a human, and is built PDPA/PDPC-aligned so customer data is handled properly.",
        "Handles rojak, mixed Malay/English/Chinese chat the way your buyers actually type.",
      ],
    },
    {
      heading: "Why Anchor Sprint",
      body: "Anchor Sprint is a Malaysian AI agency and Certified in the Claude Partner Network. We design, build, deploy and run your agent on the Claude Agent SDK and MCP — human-in-the-loop by default.",
      bullets: [
        "Built on the Claude Agent SDK + MCP, connected to the tools you already run.",
        "A public worked use-case directory and a cost calculator, so you can see the shape and the numbers before you commit.",
        "Honest buy-vs-build advice — if an off-the-shelf tool fits your case better, we will tell you.",
      ],
    },
  ],

  faqs: [
    {
      question: "Does it take WhatsApp orders end to end?",
      answer:
        "Yes. The agent can greet the buyer, confirm items against your live menu or catalogue, capture quantities and delivery details, total it up with SST/GST as configured, and write the order into your POS, order sheet or CRM. It hands anything unusual — bulk orders, special requests, complaints — to a human before it goes wrong.",
    },
    {
      question: "Can it handle Malay, English, Chinese and mixed messages?",
      answer:
        "Yes. Buyers in Malaysia and Singapore rarely stick to one language — a single message might mix Malay, English and Chinese. The agent is built to read and reply in that rojak style, so customers do not have to switch to 'proper' English to be understood.",
    },
    {
      question: "Will it charge cards on its own?",
      answer:
        "No. The agent never auto-charges a card. It sends a payment link or your usual payment instruction and the customer confirms and pays. You stay in control of the money, which keeps chargebacks and disputes down and keeps the agent's job to capturing the order, not moving funds.",
    },
    {
      question: "What systems can it push orders into?",
      answer:
        "Through MCP we connect the agent to the tools you already use — your POS, a shared order sheet, or your CRM — so an order captured on WhatsApp lands in the same place your team already works. If you are not sure your stack can connect, tell us what you run and we will check honestly before quoting.",
    },
  ],

  ctaTitle: "Losing after-hours orders on WhatsApp?",
  ctaBody:
    "Tell us your order volume and how big your menu or catalogue is, and we will say honestly whether an agent is worth building for you — no pressure either way.",
};

const ms: IndustryPageContent = {
  eyebrow: "Ejen AI · Runcit & F&B",
  h1: "Ejen AI untuk runcit & F&B di Malaysia & Singapura",
  intro:
    "Perniagaan runcit dan F&B bermargin tipis, bervolum tinggi, dan semakin banyak transaksi kini berlaku melalui WhatsApp berbanding di kaunter. Ejen AI boleh menangkap pesanan dan menjawab pembeli sepanjang masa manakala seorang manusia menguruskan kes luar biasa — dibina di atas Claude, human-in-the-loop, jadi keputusan penting kekal di tangan pasukan anda.",
  ctaLabel: "Rangka ejen untuk kedai anda",

  painHeading: "Ekonomi runcit & F&B",
  painPoints: [
    {
      title: "Margin tipis, volum tinggi, nilai kecil",
      body: "Anda buat untung daripada pusing ganti jualan, bukan daripada satu pesanan. Jadi setiap minit staf menaip semula pesanan WhatsApp ke dalam POS, menyemak stok, atau menjawab soalan yang sama kali ke-sepuluh ialah margin yang bocor senyap-senyap. Pada volum runcit dan F&B, ketidakcekapan kecil setiap pesanan cepat menjadi ringgit sebenar.",
    },
    {
      title: "WhatsApp ialah kedai anda",
      body: "Di Malaysia dan Singapura sebahagian besar pesanan dan pertanyaan tiba melalui chat — lepas waktu kerja, hujung minggu, dan serentak semasa waktu puncak. Ia datang dalam Bahasa Melayu, Inggeris dan Cina, selalunya bercampur 'rojak' dalam satu mesej. Bila tiada siapa lapang membalas dalam beberapa minit, pembeli beralih dan pesanan itu hilang begitu sahaja.",
    },
    {
      title: "Waktu puncak dan pusing ganti staf",
      body: "Musim perayaan, hari gaji dan promosi melambungkan volum, kemudian ia turun semula. Anda tak boleh ambil pekerja cukup pantas untuk waktu puncak, dan pusing ganti staf dalam runcit dan F&B tinggi — jadi anda sentiasa melatih semula staf baru tentang menu, harga dan cara anda mahu pertanyaan dijawab. Pengetahuan asyik keluar dari pintu.",
    },
    {
      title: "Konsistensi berbilang cawangan & saluran",
      body: "Mengendalikan beberapa cawangan atau menjual merentas WhatsApp, marketplace dan walk-in bermakna promo, harga dan mesej yang sama mesti sah di mana-mana, dalam setiap bahasa. Ia jarang begitu — satu cawangan beri harga lama, satu lagi terlepas tarikh tamat promo. Untuk kumpulan besar, ini merebak ke komunikasi korporat, di mana satu angka salah cepat tersebar.",
    },
  ],

  useCasesHeading: "Di mana ejen memberi pulangan dalam runcit & F&B",
  useCasesIntro:
    "Kemenangan paling jelas ialah mengambil pesanan di WhatsApp dari awal hingga akhir, menjawab pertanyaan produk dan menu, serta menyaring sokongan supaya mesej yang betul sampai kepada manusia dengan pantas. Untuk kumpulan berbilang cawangan, ejen juga mengekalkan komunikasi korporat konsisten merentas cawangan dan bahasa.",

  sections: [
    {
      heading: "Kenapa ejen sesuai untuk sektor ini",
      body: "Ejen runcit atau F&B hanya berguna jika ia mencerminkan apa yang anda jual hari ini dan tidak sekali-kali melampaui had berkaitan wang. Kami bina supaya ia bekerja daripada sistem langsung anda dan menyerahkan tugas sebaik sahaja sesuatu kelihatan tak kena.",
      bullets: [
        "Bekerja daripada menu, katalog dan stok langsung anda, jadi ia hanya tawar apa yang benar-benar ada — takkan jual barang yang sudah habis.",
        "Menulis pesanan melalui alat yang disambung (POS, helaian pesanan, CRM) supaya tiada salah taip semasa dimasukkan.",
        "Tidak sekali-kali caj kad secara automatik — ia hantar pautan bayaran dan pelanggan yang putuskan; anda kekal kawal wang.",
        "Menghala pesanan ganjil atau bernilai tinggi, bayaran balik dan aduan kepada manusia, dan dibina selaras PDPA/PDPC supaya data pelanggan diuruskan dengan betul.",
        "Mengendalikan chat rojak bercampur Melayu/Inggeris/Cina cara pembeli anda memang menaip.",
      ],
    },
    {
      heading: "Kenapa Anchor Sprint",
      body: "Anchor Sprint ialah agensi AI Malaysia dan diperakui dalam Claude Partner Network. Kami reka, bina, lancar dan jalankan ejen anda di atas Claude Agent SDK dan MCP — human-in-the-loop secara lalai.",
      bullets: [
        "Dibina di atas Claude Agent SDK + MCP, disambung kepada alat yang sudah anda guna.",
        "Direktori kes guna yang telah dikerjakan secara awam dan kalkulator kos, jadi anda nampak bentuk dan angka sebelum komit.",
        "Nasihat buy-vs-build yang jujur — jika alat siap sedia lebih sesuai untuk kes anda, kami akan beritahu.",
      ],
    },
  ],

  faqs: [
    {
      question: "Adakah ia ambil pesanan WhatsApp dari awal hingga akhir?",
      answer:
        "Ya. Ejen boleh menyapa pembeli, mengesahkan item berdasarkan menu atau katalog langsung anda, menangkap kuantiti dan butiran penghantaran, menjumlahkan dengan SST/GST seperti ditetapkan, dan menulis pesanan ke dalam POS, helaian pesanan atau CRM anda. Ia serahkan apa-apa yang luar biasa — pesanan pukal, permintaan khas, aduan — kepada manusia sebelum ia jadi masalah.",
    },
    {
      question: "Bolehkah ia kendalikan mesej Melayu, Inggeris, Cina dan bercampur?",
      answer:
        "Ya. Pembeli di Malaysia dan Singapura jarang berpegang pada satu bahasa — satu mesej mungkin bercampur Melayu, Inggeris dan Cina. Ejen dibina untuk membaca dan membalas dalam gaya rojak itu, jadi pelanggan tak perlu tukar kepada Inggeris 'betul' untuk difahami.",
    },
    {
      question: "Adakah ia akan caj kad dengan sendiri?",
      answer:
        "Tidak. Ejen tidak sekali-kali caj kad secara automatik. Ia hantar pautan bayaran atau arahan bayaran biasa anda dan pelanggan mengesahkan serta membayar. Anda kekal kawal wang, yang mengurangkan chargeback dan pertikaian, dan mengekalkan tugas ejen kepada menangkap pesanan, bukan menggerakkan wang.",
    },
    {
      question: "Sistem apa yang ia boleh masukkan pesanan?",
      answer:
        "Melalui MCP kami sambungkan ejen kepada alat yang sudah anda guna — POS anda, helaian pesanan berkongsi, atau CRM anda — jadi pesanan yang ditangkap di WhatsApp mendarat di tempat sama pasukan anda sudah bekerja. Jika anda tak pasti stack anda boleh disambung, beritahu kami apa yang anda guna dan kami akan semak dengan jujur sebelum beri sebut harga.",
    },
  ],

  ctaTitle: "Hilang pesanan lepas waktu kerja di WhatsApp?",
  ctaBody:
    "Beritahu kami volum pesanan anda dan sebesar mana menu atau katalog anda, dan kami akan cakap dengan jujur sama ada ejen berbaloi dibina untuk anda — tiada tekanan.",
};

const zh: IndustryPageContent = {
  eyebrow: "AI 智能体 · 零售与餐饮",
  h1: "为马来西亚与新加坡零售与餐饮打造的 AI 智能体",
  intro:
    "零售与餐饮是利润薄、走量大的生意，而如今越来越多交易发生在 WhatsApp 上，而不是柜台前。AI 智能体可以全天候接单并回应买家，把特殊情况交给人工处理——基于 Claude 构建，human-in-the-loop，关键判断仍由您的团队掌握。",
  ctaLabel: "为您的门店规划一个智能体",

  painHeading: "零售与餐饮的经济账",
  painPoints: [
    {
      title: "利润薄、走量大、客单低",
      body: "您靠的是销售流水赚钱，而不是某一单。所以员工每花一分钟把 WhatsApp 订单重新录入 POS、去查库存、或第十次回答同一个问题，都是在悄悄流失利润。以零售与餐饮的走量来看，每单的小小低效很快就累积成实实在在的 RM。",
    },
    {
      title: "WhatsApp 就是您的店面",
      body: "在马来西亚与新加坡，很大一部分订单和咨询都来自聊天——下班后、周末、以及高峰期一齐涌来。它们用马来语、英语和中文而来，往往在一条消息里夹杂 'rojak'。当没人能在几分钟内回复时，买家转身就走，订单就这样无声无息地丢了。",
    },
    {
      title: "高峰与人员流动",
      body: "佳节、发薪日和促销让单量暴增，随后又回落。您没法为高峰足够快地招人，而零售与餐饮的人员流动又高——于是您永远在给新员工重新培训菜单、价格以及您希望的应答方式。经验总是随人走出门外。",
    },
    {
      title: "多门店与多渠道的一致性",
      body: "经营多家门店，或横跨 WhatsApp、电商平台与到店销售，意味着同样的促销、价格和话术必须在各处、各种语言中都保持一致。但往往做不到——一家门店报的是旧价，另一家漏掉了促销截止日期。对较大的集团来说，这会蔓延到企业传讯，一个错误数字会传得飞快。",
    },
  ],

  useCasesHeading: "智能体在零售与餐饮中的回报点",
  useCasesIntro:
    "最明确的收益在于：在 WhatsApp 上端到端接单、回答产品与菜单咨询，以及分流客服，让正确的消息快速送达人工。对于多门店集团，智能体还能让企业传讯在各门店与各语言之间保持一致。",

  sections: [
    {
      heading: "为什么智能体适合这个行业",
      body: "零售或餐饮智能体只有在真实反映您今天所卖的东西、并且绝不在钱上越界时才有用。我们让它基于您的实时系统运作，一旦发现异常就立刻转交人工。",
      bullets: [
        "基于您的实时菜单、目录和库存运作，因此只推荐真正有货的——绝不卖已售罄的商品。",
        "通过已连接的工具（POS、订单表、CRM）写入订单，录入时不会打错字。",
        "绝不自动扣卡——它发送支付链接，由客户决定；钱始终在您掌控之中。",
        "把异常或高价值订单、退款和投诉转交人工，并按 PDPA/PDPC 对齐构建，妥善处理客户数据。",
        "以您的买家真正打字的方式，处理夹杂马来语/英语/中文的 rojak 聊天。",
      ],
    },
    {
      heading: "为什么选择 Anchor Sprint",
      body: "Anchor Sprint 是一家马来西亚 AI 机构，也是 Claude Partner Network 认证合作伙伴。我们在 Claude Agent SDK 和 MCP 上设计、构建、部署并运行您的智能体——默认 human-in-the-loop。",
      bullets: [
        "基于 Claude Agent SDK + MCP 构建，连接您已在使用的工具。",
        "公开的实作用例目录和成本计算器，让您在投入前就看清形态与数字。",
        "诚实的 buy-vs-build 建议——如果现成工具更适合您的场景，我们会直说。",
      ],
    },
  ],

  faqs: [
    {
      question: "它能端到端处理 WhatsApp 订单吗？",
      answer:
        "可以。智能体能问候买家、依据您的实时菜单或目录确认商品、采集数量与配送信息、按设定加上 SST/GST 计算总额，并把订单写入您的 POS、订单表或 CRM。遇到不寻常的情况——大批量订单、特殊要求、投诉——它会在出错之前交给人工。",
    },
    {
      question: "它能处理马来语、英语、中文以及混合消息吗？",
      answer:
        "可以。马来西亚与新加坡的买家很少只用一种语言——一条消息可能夹杂马来语、英语和中文。智能体被构建为以这种 rojak 风格阅读和回复，客户无需切换到 '标准' 英语也能被理解。",
    },
    {
      question: "它会自己扣卡吗？",
      answer:
        "不会。智能体绝不自动扣卡。它发送支付链接或您惯用的付款指示，由客户确认并付款。钱始终在您掌控之中，这能减少拒付与纠纷，也让智能体的职责停留在接单，而不是转移资金。",
    },
    {
      question: "它能把订单推送到哪些系统？",
      answer:
        "通过 MCP，我们把智能体连接到您已在使用的工具——您的 POS、共享订单表或 CRM——这样在 WhatsApp 上接到的订单会落在您团队已经工作的地方。如果您不确定自己的系统能否连接，把您在用的告诉我们，我们会在报价前诚实核实。",
    },
  ],

  ctaTitle: "正在 WhatsApp 上丢失下班后的订单？",
  ctaBody:
    "把您的订单量以及菜单或目录的规模告诉我们，我们会诚实地判断为您构建一个智能体是否值得——无论如何都没有压力。",
};

export const retailIndustry: IndustryPage = {
  key: "retail",
  route: "solutions/ai-agents-for-retail",
  heroImage: "/images/industries/retail-hero.jpg",
  cardLabel: { en: "Retail & F&B", ms: "Runcit & F&B", zh: "零售与餐饮" },
  useCaseSlugs: [
    "order-processing",
    "product-enquiry",
    "support-triage",
    "corporate-comms",
  ],
  content: { en, ms, zh },
};
